<script setup lang="ts">
import * as zod from 'zod'
import type { TypedSchema } from 'vee-validate'
import type { NeedHelpMailRequest } from '~/types/api'

const props = defineProps<{
  setNid: number
  productTitle: string
}>()

const emits = defineEmits(['close'])

const countriesStore = useCountriesStore()
await countriesStore.loadCountries()
await countriesStore.loadRegions()

const { countries, countriesLoading, regions, regionsLoading } =
  storeToRefs(countriesStore)

const isCountryDividedToRegions: ComputedRef<boolean> = computed(() => {
  const countriesWithRegions = Object.keys(regions.value)
  return countriesWithRegions.includes(country.value as string)
})

const name = 'need-help-form'
const { t } = useI18n()
const userStore = useUser()

const { requiredString, optionalString, optionalInt, requiredInt } = useRules()

const validationSchema: ComputedRef<TypedSchema> = computed(() => {
  return toTypedSchema(
    zod.object({
      email: requiredString().email({
        message: t('base.form.emailNotValid'),
      }),
      country: requiredString(),
      region: optionalString(),
      message: requiredString(),
    })
  )
})

const { validate, errors } = useForm({
  validationSchema,
})

const { getCaptchaToken } = useVueRecaptcha()
const route = useRoute()
const toast = useToast()

const { sendNeedHelpEvent, sendNeedHelpSentEvent } = useEvent()

onMounted(() => {
  sendNeedHelpEvent(props.productTitle)
})

async function onSubmit() {
  const valid = await validate()
  if (!valid) {
    return
  }
  const recaptchaToken = await getCaptchaToken('helpForm')
  const data = {
    email: email.value,
    message: message.value,
    countryId: parseInt(country.value, 10),
    regionId: region.value ? parseInt(region.value, 10) : null,
    originUrl: route.path,
    recaptchaToken: recaptchaToken,
    productSetId: props.setNid,
  } as NeedHelpMailRequest
  try {
    await createMailRepository().sendNeedHelp(data)
    sendNeedHelpSentEvent(props.productTitle)
    emits('close')
  } catch (err) {
    console.error(err)
    toast.add({
      title: t('base.order.error'),
      description: t('base.order.help.errorInfo'),
      color: 'red',
    })
  }
}

const { value: email } = useField<string>('email')
const { value: country } = useField<string>('country')
const { value: region } = useField<string>('region')
const { value: message } = useField<string>('message')

const userCountry = await countriesStore.getCountryIdForCode(
  userStore.user?.country
)

if (userStore.user) {
  email.value = userStore.user.email
  country.value = userCountry?.nid.toString()
  region.value = userStore.user.regionId?.toString()
}
</script>

<template>
  <div class="w-full">
    <div class="flex justify-end">
      <Icon
        name="material-symbols-light:close-rounded"
        size="30"
        class="m-3 cursor-pointer"
        @click="emits('close')"
      />
    </div>
    <div class="flex justify-center px-10">
      <div class="font-pragmatica-ext text-3xl font-bold">
        {{ $t('base.products.help.title') }}
      </div>
    </div>
    <div
      class="m-10 flex flex-col justify-center gap-5 bg-neutral-100 p-10 align-middle"
    >
      <div class="flex justify-center">
        {{ $t('base.products.help.contact') }}
      </div>
      <div
        class="font-pragmatica-ext flex justify-center whitespace-nowrap text-lg font-bold sm:text-2xl"
      >
        {{ $t('base.products.help.number') }}
      </div>
      <div class="flex justify-center">
        {{ $t('base.products.help.formInfo') }}
      </div>
    </div>
    <div>
      <div class="flex flex-col gap-10 p-10">
        <div class="font-pragmatica-ext text-2xl font-bold">
          {{ $t('base.products.help.form.title') }}
        </div>
        <form @submit.prevent="onSubmit" :validation-schema="validationSchema">
          <FormFieldInput
            v-model="email"
            name="email"
            :parent-name="name"
            property-name="email"
            label="signup.email"
            icon="envelope"
            required
            :error="errors.email"
            autocomplete="username"
          />
          <FormFieldInput
            v-model="country"
            name="country"
            :parent-name="name"
            property-name="country"
            type="select"
            label="signup.country"
            icon="globe"
            :items="countries"
            :items-loading="countriesLoading || regionsLoading"
            required
            :error="errors.country"
          />
          <Transition
            enter-active-class="transition-all duration-500 ease-out"
            leave-active-class="transition-all duration-500 ease-in"
            enter-from-class="translate-y-[-20px] opacity-0"
            leave-to-class="translate-y-[-20px] opacity-0"
            appear
          >
            <FormFieldInput
              v-show="isCountryDividedToRegions"
              v-model="region"
              name="region"
              :parent-name="name"
              property-name="region"
              type="select"
              icon="flag"
              label="signup.region"
              :items="regions[country]"
              :items-loading="regionsLoading"
              required
              :error="errors.region"
            />
          </Transition>
          <FormFieldInput
            v-model="message"
            name="help-message"
            :parent-name="name"
            property-name="help-message"
            label="base.products.help.form.message"
            icon="envelope"
            required
            :error="errors.message"
            align-start
            type="textarea"
          />
          <button
            type="submit"
            class="text-pragmatica-extended mt-10 block w-full rounded-sm bg-black px-4 py-2 text-2xl font-medium text-white"
          >
            <span v-if="true" class="-mt-1 flex items-center justify-center">
              {{ $t('base.products.help.submit') }}
            </span>
            <Icon v-else name="line-md:loading-twotone-loop" />
          </button>
          <UiRecaptchaText class="pt-1" />
        </form>
      </div>
    </div>
  </div>
</template>
